import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  Activity, AlertTriangle, CheckCircle2, Phone, Mail, UserPlus,
  Calendar, FileText, Clock,
} from "lucide-react";
import type { DbLead } from "@/lib/types";

interface StaffActivityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staffId: string;
  staffName: string;
  staffRole: string;
  leads: DbLead[]; // all agency leads — we filter to assigned
}

interface ActivityRow {
  id: string;
  action_type: string;
  lead_id: string | null;
  created_at: string;
}

const ACTION_LABEL: Record<string, { label: string; icon: any; color: string }> = {
  lead_assigned:   { label: "Was assigned a lead",       icon: UserPlus,     color: "text-primary" },
  lead_contacted:  { label: "Contacted lead",            icon: Phone,        color: "text-success" },
  email_sent:      { label: "Sent email",                icon: Mail,         color: "text-primary" },
  call_logged:     { label: "Logged a call",             icon: Phone,        color: "text-success" },
  status_updated:  { label: "Updated lead status",       icon: FileText,     color: "text-muted-foreground" },
  lead_booked:     { label: "Booked a discovery call",   icon: Calendar,     color: "text-success" },
  note_added:      { label: "Added a note",              icon: FileText,     color: "text-muted-foreground" },
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function isStale(lead: DbLead): boolean {
  // Hot lead (≥85) created >24h ago that is still "new"
  const ageH = (Date.now() - new Date(lead.created_at).getTime()) / 3_600_000;
  return lead.score >= 85 && ageH > 24 && lead.status === "new";
}

export default function StaffActivityModal({
  open, onOpenChange, staffId, staffName, staffRole, leads,
}: StaffActivityModalProps) {
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !staffId) return;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("staff_activity_log")
        .select("id, action_type, lead_id, created_at")
        .eq("user_id", staffId)
        .order("created_at", { ascending: false })
        .limit(50);
      setActivity((data ?? []) as ActivityRow[]);
      setLoading(false);
    })();
  }, [open, staffId]);

  const assignedLeads = useMemo(
    () => leads.filter(l => (l as any).assigned_to === staffId),
    [leads, staffId],
  );

  const metrics = useMemo(() => {
    const now = Date.now();
    const week = 7 * 86_400_000;
    const recent = activity.filter(a => now - new Date(a.created_at).getTime() < week);
    const contacted = recent.filter(a =>
      ["lead_contacted", "email_sent", "call_logged"].includes(a.action_type)
    ).length;
    const booked = assignedLeads.filter(l => l.status === "booked").length;
    const total = assignedLeads.length;
    const conversionPct = total > 0 ? Math.round((booked / total) * 100) : 0;
    const stale = assignedLeads.filter(isStale).length;
    return { contacted, booked, total, conversionPct, stale, assignmentsWeek: recent.filter(a => a.action_type === "lead_assigned").length };
  }, [activity, assignedLeads]);

  const initials = staffName.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-muted-foreground text-card flex items-center justify-center text-sm font-bold">
              {initials}
            </div>
            <div>
              <div className="text-lg font-bold text-navy">{staffName}</div>
              <div className="text-xs text-muted-foreground font-normal capitalize">{staffRole} · activity & performance</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Performance metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
          <MetricTile label="Assigned leads" value={metrics.total} />
          <MetricTile label="Contacted (7d)" value={metrics.contacted} accent="text-primary" />
          <MetricTile label="Booked" value={metrics.booked} accent="text-success" />
          <MetricTile label="Conversion" value={`${metrics.conversionPct}%`} accent={metrics.conversionPct >= 30 ? "text-success" : "text-warning"} />
        </div>

        {metrics.stale > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-hot/30 bg-hot/5 p-3 mt-3">
            <AlertTriangle className="h-4 w-4 text-hot shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-bold text-hot">{metrics.stale} stale hot lead{metrics.stale > 1 ? "s" : ""}</span>
              <span className="text-muted-foreground"> — hot leads ({">"} 85 score) sitting more than 24h without contact.</span>
            </div>
          </div>
        )}

        {/* Assigned leads */}
        <div className="mt-4">
          <h3 className="font-bold text-navy text-sm mb-2">Assigned Leads ({assignedLeads.length})</h3>
          {assignedLeads.length === 0 ? (
            <div className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground">
              No leads assigned yet.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {assignedLeads.map(lead => {
                const stale = isStale(lead);
                return (
                  <div
                    key={lead.id}
                    className={`flex items-center justify-between gap-3 rounded-lg border p-2.5 text-sm ${stale ? "bg-hot/5 border-hot/30" : "bg-card"}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-navy truncate">{lead.name}</span>
                        {stale && <AlertTriangle className="h-3.5 w-3.5 text-hot shrink-0" />}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Score {lead.score} · {lead.zip_code || "—"} · {timeAgo(lead.created_at)}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={
                        lead.status === "booked" ? "border-success text-success" :
                        lead.status === "contacted" ? "border-primary text-primary" :
                        lead.status === "lost" ? "border-muted-foreground text-muted-foreground" :
                        "border-warning text-warning"
                      }
                    >
                      {lead.status}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="mt-4">
          <h3 className="font-bold text-navy text-sm mb-2 flex items-center gap-1.5">
            <Activity className="h-4 w-4" /> Recent Activity
          </h3>
          {loading ? (
            <div className="py-6 flex justify-center">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gold border-t-transparent" />
            </div>
          ) : activity.length === 0 ? (
            <div className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground">
              No recorded activity yet.
            </div>
          ) : (
            <ol className="relative border-l border-border ml-2 space-y-3 max-h-64 overflow-y-auto">
              {activity.map(a => {
                const meta = ACTION_LABEL[a.action_type] ?? { label: a.action_type, icon: CheckCircle2, color: "text-muted-foreground" };
                const Icon = meta.icon;
                return (
                  <li key={a.id} className="ml-4">
                    <span className={`absolute -left-[7px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-card border-2 border-border ${meta.color}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    </span>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                      <span className="text-navy font-medium">{meta.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Clock className="h-3 w-3" /> {timeAgo(a.created_at)}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetricTile({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className={`text-2xl font-bold ${accent ?? "text-navy"}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
